import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const EXAMPLE_GUESSES = [
  { name: 'Dog', rank: 3, emoji: '🟠', note: 'Way too obvious' },
  { name: 'Otter', rank: 58, emoji: '🟢', note: 'Solid pick' },
  { name: 'Axolotl', rank: 96, emoji: '🎯', note: 'Deep cut!' },
];

export function HowToPlayModal({ onClose }: { onClose: () => void }) {
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 16 }}
          transition={{ type: 'spring', stiffness: 260, damping: 22 }}
          className="w-full max-w-md bg-background border-2 border-border rounded-3xl shadow-2xl relative max-h-[85vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Sticky top bar so close is always reachable */}
          <div className="sticky top-0 z-10 bg-background/90 backdrop-blur border-b border-border px-6 py-4 flex items-center justify-center">
            <div className="text-sm font-semibold text-foreground/50 uppercase tracking-widest">How to Play</div>
            <button
              onClick={onClose}
              className="absolute right-4 top-3 p-2 rounded-full hover:bg-muted transition-colors"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(85vh-56px)] space-y-6">
            {/* The one thing to understand */}
            <div className="text-center">
              <div className="text-2xl font-black tracking-tight leading-tight">
                Guess answers closest to <span className="whitespace-nowrap">#100</span>
              </div>
              <div className="text-sm text-foreground/60 mt-1 leading-relaxed">
                Every topic has a Top 100. The obvious picks rank low — the hidden gems near #100 are worth big points.
              </div>
            </div>

            {/* Rank spectrum: where to aim */}
            <div>
              <div className="flex justify-end mb-1.5">
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, type: 'spring', stiffness: 300, damping: 18 }}
                  className="text-[10px] font-black tracking-widest bg-foreground text-background px-2 py-0.5 rounded-md"
                >
                  AIM HERE ↓
                </motion.div>
              </div>
              <div className="h-3 rounded-full bg-[linear-gradient(to_right,#fb923c,#facc15,#4ade80,#22d3ee)]" />
              <div className="flex justify-between mt-2 text-[10px] font-black uppercase tracking-widest">
                <div className="text-left text-foreground/40">
                  <div>#1</div>
                  <div>Everyone knows it</div>
                </div>
                <div className="text-right text-foreground">
                  <div>#100</div>
                  <div>Hidden gem</div>
                </div>
              </div>
            </div>

            {/* Example round */}
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40 mb-2 text-center">
                Example — Top 100 Animals
              </div>
              <div className="space-y-2">
                {EXAMPLE_GUESSES.map((g, i) => (
                  <motion.div
                    key={g.name}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.55 + i * 0.18 }}
                    className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-muted/40 px-4 py-3"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="text-xl">{g.emoji}</div>
                      <div className="min-w-0">
                        <div className="font-bold truncate">{g.name}</div>
                        <div className="text-[11px] font-medium text-foreground/50">{g.note}</div>
                      </div>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <div className="font-black tabular-nums">+{g.rank}</div>
                      <div className="text-[11px] font-medium text-foreground/50">ranked #{g.rank}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
              <div className="text-xs text-foreground/50 text-center mt-2">
                A guess's rank = its points. Not on the list? 0 points.
              </div>
            </div>

            {/* The rules, at a glance */}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-2xl border border-border bg-muted/40 py-3 text-center">
                <div className="text-2xl font-black tabular-nums">5</div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40">Guesses</div>
              </div>
              <div className="rounded-2xl border border-border bg-muted/40 py-3 text-center">
                <div className="text-2xl font-black tabular-nums">490</div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40">Max score</div>
              </div>
              <div className="rounded-2xl border border-border bg-muted/40 py-3 text-center">
                <div className="text-2xl font-black">Daily</div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40">New topic</div>
              </div>
            </div>

            <div className="text-xs text-foreground/50 text-center leading-relaxed">
              💡 Typos are forgiven · One free hint per puzzle · Be specific, abbreviations usually don’t count
            </div>

            <button
              onClick={onClose}
              className="w-full bg-foreground text-background py-3.5 rounded-xl font-bold text-lg hover:opacity-90 transition-all active:scale-[0.98]"
            >
              Got it — let’s play
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
